import React, { useState, useEffect, useRef } from 'react';
import { friendlyDeleteError } from '../utils/deleteError';
import { useMoney } from '../hooks/useMoney';
import { RecordIdLabel } from './ui/idColumn';
import { useNavigate } from 'react-router-dom';
import ContentCard from './ContentCard';
import RecordActivity from './ui/RecordActivity';
import AttachmentsPanel from './ui/AttachmentsPanel';
import { useRole } from '../hooks/useRole';
import BundleMetricCards from './BundleMetricCards';
import EmptyState from './EmptyState';
import ConfirmDialog from './ui/ConfirmDialog';
import { CurrencyInput } from './ui/CurrencyInput';
import SearchableSelect from './ui/SearchableSelect';
import RichTextEditor from './ui/RichTextEditor';
import { UnifiedBundle, UnifiedBundleItem } from '../types/Product';
import {
  loadBundles, createBundle, updateBundle, deleteBundle, refreshBundlesFromApi, fetchArchivedBundles,
  fetchTrashedBundles, archiveBundle, unarchiveBundle, trashBundle, restoreBundleFromTrash,
} from '../services/BundleService';
import { loadBundleAllocations, computeBundleActualCost, computeBundleComponentCosts, BundleComponentCost } from '../services/ItemAllocationService';
import { CURRENCY_OPTIONS, formatUnitCost } from '../utils/currency';
import { filterProductsByType, getProduct, getProducts, refreshProductsFromApi } from '../services/ProductService';
import { Product, ProductType } from '../types/Product';
import { useNotifications } from './NotificationProvider';
import LineItemsEditor from './ui/LineItemsEditor';
import BundleEconomicsPanel from './BundleEconomicsPanel';
import '../styles/components/BundlesView.css';
import '../styles/components/DataTable.css';
import RecordListPublisher from './RecordListPublisher';

// Every unit a bundle line item might use — real fabric is always yards,
// a picked favour product or free-typed "other" item can be any of these.
const LINE_ITEM_UNITS = ['yds', 'pcs', 'sets', 'boxes', 'bags', 'units'];

const genId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 6)}`;

/** Looks up a Contents row's real landed unit cost by matching its display
 *  name against the bundle's per-component costs (see the `cost` field
 *  wiring on the Contents LineItemsEditor for why name is the join key).
 *  Blank when there's no real procurement data for this row yet — a blank
 *  cell is an honest "not sourced yet", not a fabricated zero. */
const formatComponentCost = (
  itemName: string,
  components: BundleComponentCost[],
  currency: string
): string => {
  const normalized = itemName.trim().toLowerCase();
  if (!normalized) return '';
  const match = components.find(c => c.productName.trim().toLowerCase() === normalized);
  return match ? formatUnitCost(match.landedUnitCost, currency) : '';
};

/** Renders a bundle's per-unit cost target in its own currency. The target is
 *  held on `bundles.target_cost`/`target_currency`; before this it was invisible
 *  in the app, which is why bundle costings were being explained in prose. */
const formatTargetCost = (bundle: UnifiedBundle, fallbackCurrency: string): string => {
  if (bundle.targetCost == null) return '';
  return formatUnitCost(bundle.targetCost, bundle.targetCurrency ?? fallbackCurrency);
};

interface BundlesViewProps {
  /** Bundle id parsed from the /bundle/:id URL (App.tsx) — auto-opens the
   * edit form for this bundle when landing directly on that route. */
  openBundleId?: string;
  /** True when landing directly on /bundle/new — auto-opens the create form. */
  openNewBundleOnMount?: boolean;
  /** Which ProductType this view lists, filters stats by, and tags new/
   * untyped bundles with on save. Defaults to 'asoebi' since every existing
   * caller (AsoebiManager, and this component's own default route) is the
   * asoebi bundle view. */
  scopeType?: ProductType;
}

const BundlesView: React.FC<BundlesViewProps> = ({ openBundleId, openNewBundleOnMount, scopeType = 'asoebi' }) => {
  const money = useMoney();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotifications();
  const { canEdit } = useRole();

  const [bundles, setBundles] = useState<UnifiedBundle[]>(() => loadBundles());
  const [favourProducts, setFavourProducts] = useState<Product[]>(() => filterProductsByType('favour'));
  // Anything in the products catalogue that isn't a favour product —
  // fabrics and accessories like the AOE Cooler that an asoebi bundle pulls
  // in. The separate fabric picker went with the Asoebi screens (its
  // /asoebi/fabrics API is disabled), so fabric products are picked here.
  const [otherProducts, setOtherProducts] = useState<Product[]>(
    () => getProducts().filter(p => !p.archived && !p.types.includes('favour'))
  );
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  // 4-state (active/archived/trashed/all), matching Venues/DecorPlanner/
  // ProductCatalog's active/archived/all convention plus a 'trashed' bucket
  // for stage 2 of the archive → trash → permanent-delete lifecycle — a
  // trashed bundle is excluded from 'active', 'archived' and 'all' alike,
  // and only shows up when this filter is explicitly set to 'trashed'.
  const [archiveFilter, setArchiveFilter] = useState<'active' | 'archived' | 'trashed' | 'all'>('active');
  const [archivedBundles, setArchivedBundles] = useState<UnifiedBundle[]>([]);
  const [trashedBundles, setTrashedBundles] = useState<UnifiedBundle[]>([]);

  useEffect(() => {
    void refreshBundlesFromApi().then(setBundles);
    void refreshProductsFromApi().then(() => {
      setFavourProducts(filterProductsByType('favour'));
      setOtherProducts(getProducts().filter(p => !p.archived && !p.types.includes('favour')));
    });
  }, []);

  // Only fetched when actually needed — most visits never touch the archived/trashed view.
  useEffect(() => {
    if (archiveFilter === 'active') return;
    if (archiveFilter === 'trashed') { void fetchTrashedBundles().then(setTrashedBundles).catch(() => {}); return; }
    void fetchArchivedBundles().then(setArchivedBundles).catch(() => {});
  }, [archiveFilter]);

  // Lives in Header's breadcrumb alongside the view-mode toggle, same
  // sync/set CustomEvent convention as bundle:view-mode.
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('bundle-archived-sync', { detail: archiveFilter }));
  }, [archiveFilter]);

  useEffect(() => {
    const onSet = (e: Event) => setArchiveFilter((e as CustomEvent).detail);
    window.addEventListener('bundle-archived-set', onSet);
    return () => window.removeEventListener('bundle-archived-set', onSet);
  }, []);

  const [showForm, setShowForm] = useState(false);
  const [editingBundle, setEditingBundle] = useState<UnifiedBundle | null>(null);
  // Actual landed cost/unit for whatever's actually been procured and
  // allocated to this bundle, vs. its target_cost sourcing ceiling — see
  // computeBundleActualCost for why this needs a fetch (allocations span
  // however many procurement orders the bundle was sourced across).
  const [bundleActualCost, setBundleActualCost] = useState<{ actualCostPerUnit: number; allocatedQuantity: number } | null>(null);
  // Per-component landed unit costs (Brandy XO £x, Handcream £y, …) — shown
  // read-only next to each Contents row's Qty. Matched to a bundle.items row
  // by product name (normalized), the only link that reliably exists: a
  // procurement_item_allocations row references procurement_items.id, which
  // is a different id space than bundle.items[].productId (often a synthetic
  // id for fabric/favour references), but this session's own linking work
  // established product_name as matching exactly between the two.
  const [bundleComponentCosts, setBundleComponentCosts] = useState<BundleComponentCost[]>([]);
  useEffect(() => {
    // Real landed cost comes from procurement allocations already tied to
    // this bundle (bundle_id) — it exists independently of whether a
    // target_cost has ever been set, so it must not be gated on one. A
    // target only adds an over/under comparison once both exist.
    if (!editingBundle?.id) { setBundleActualCost(null); setBundleComponentCosts([]); return; }
    let cancelled = false;
    loadBundleAllocations(editingBundle.id)
      .then(rows => {
        if (cancelled) return;
        const currency = editingBundle.targetCurrency ?? money.currency;
        setBundleActualCost(computeBundleActualCost(rows, currency));
        setBundleComponentCosts(computeBundleComponentCosts(rows, currency));
      })
      .catch(() => { if (!cancelled) { setBundleActualCost(null); setBundleComponentCosts([]); } });
    return () => { cancelled = true; };
  }, [editingBundle?.id, editingBundle?.targetCurrency]);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [draft, setDraft] = useState<{
    name: string;
    description: string;
    price: string;
    // One row per bundle item — productId set when picked from the fabric/
    // favour catalogue, empty (with name holding the typed text) for a
    // free-typed item like "Gele / Headtie" that isn't a real product.
    items: Array<{ id: string; productId: string; name: string; quantity: string; unit: string }>;
    paymentReference: string;
    paymentInstructions: string;
    // Sourcing cost ceiling per unit — separate from `price` (what's charged
    // to the recipient). Empty string means "no target set", not zero.
    // Existing bundles can be denominated in whatever currency their
    // procurement was actually costed in (e.g. USD for a China-sourced
    // item) — never assume NGN.
    targetCost: string;
    targetCurrency: string;
  }>({ name: '', description: '', price: '', items: [{ id: genId(), productId: '', name: '', quantity: '', unit: 'yds' }],
       paymentReference: '', paymentInstructions: '', targetCost: '', targetCurrency: money.currency });

  const openNewBundle = () => {
    setEditingBundle(null);
    setDraft({ name: '', description: '', price: '', items: [{ id: genId(), productId: '', name: '', quantity: '', unit: 'yds' }],
               paymentReference: '', paymentInstructions: '', targetCost: '', targetCurrency: money.currency });
    setShowForm(true);
    window.dispatchEvent(new CustomEvent('bundle:form-open', { detail: { isEdit: false } }));
    navigate('/bundle/new');
  };

  const openEditBundle = (bundle: UnifiedBundle) => {
    setEditingBundle(bundle);
    // Reconnect each saved item to a real catalogue entry where possible —
    // a real fabric's id survives as-is, but a picked favour product was
    // historically saved under a synthetic id (see saveBundle), so that
    // case can only be re-matched by name. Anything else with a real,
    // resolvable product id (any type, not just favour) is reconnected by id
    // too — this used to fall through to the free-typed branch below, which
    // strips the id and mints a fresh random one on save, silently orphaning
    // the line from its real product (this is what broke General Asoebi -
    // Women (Alfa)'s Gele/AOE Cooler links). Only a genuinely free-typed item
    // with no matching product anywhere falls through now.
    const itemsMapped = bundle.items.map(i => {
      const prod = favourProducts.find(p => p.name === i.productName);
      if (prod) return { id: genId(), productId: `p:${prod.id}`, name: prod.name, quantity: String(i.quantity), unit: i.unitLabel || 'pcs' };
      const anyProd = getProduct(i.productId);
      if (anyProd) return { id: genId(), productId: `g:${anyProd.id}`, name: anyProd.name, quantity: String(i.quantity), unit: i.unitLabel || 'pcs' };
      return { id: genId(), productId: '', name: i.productName, quantity: String(i.quantity), unit: i.unitLabel || 'pcs' };
    });
    setDraft({
      name: bundle.name,
      description: bundle.description ?? '',
      paymentReference: bundle.paymentReference ?? '',
      paymentInstructions: bundle.paymentInstructions ?? '',
      price: String(bundle.price),
      items: itemsMapped.length > 0 ? itemsMapped : [{ id: genId(), productId: '', name: '', quantity: '', unit: 'yds' }],
      targetCost: bundle.targetCost != null ? String(bundle.targetCost) : '',
      targetCurrency: bundle.targetCurrency ?? money.currency,
    });
    setShowForm(true);
    window.dispatchEvent(new CustomEvent('bundle:form-open', {
      detail: { isEdit: true, name: bundle.name, archived: !!bundle.archived, trashed: !!bundle.deletedAt },
    }));
    navigate(`/bundle/${bundle.id}`);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingBundle(null);
    window.dispatchEvent(new CustomEvent('bundle:form-close'));
    // A bundle record is reachable from more than one surface (Asoebi &
    // Style, Favours, …) — hardcoding '/asoebi' here sent Cancel back to
    // the wrong place whenever the bundle was opened from anywhere else.
    // Going back in history returns to wherever the user actually came from.
    navigate(-1);
  };

  // Auto-open the create/edit form when landing directly on /bundle/new or
  // /bundle/:id (App.tsx parses the URL and passes these down) — otherwise
  // the URL and breadcrumb would update on navigate() but the form itself
  // would never actually open.
  useEffect(() => {
    if (openNewBundleOnMount && !showForm) {
      openNewBundle();
      return;
    }
    if (openBundleId && (!editingBundle || editingBundle.id !== openBundleId)) {
      const match = bundles.find(b => b.id === openBundleId);
      if (match) openEditBundle(match);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openNewBundleOnMount, openBundleId, bundles]);

  const saveBundleRef = useRef<() => void>(() => {});
  const closeFormRef = useRef<() => void>(() => {});
  const openNewBundleRef = useRef<() => void>(() => {});

  const deleteBundleRef = useRef<() => void>(() => {});
  const archiveBundleRef = useRef<() => void>(() => {});
  const unarchiveBundleRef = useRef<() => void>(() => {});
  const trashBundleRef = useRef<() => void>(() => {});
  const restoreBundleRef = useRef<() => void>(() => {});

  useEffect(() => {
    const handleNew = () => openNewBundleRef.current();
    const handleSave = () => saveBundleRef.current();
    const handleCancel = () => closeFormRef.current();
    const handleDelete = () => deleteBundleRef.current();
    const handleArchive = () => archiveBundleRef.current();
    const handleUnarchive = () => unarchiveBundleRef.current();
    const handleTrash = () => trashBundleRef.current();
    const handleRestore = () => restoreBundleRef.current();
    const handleViewMode = (e: Event) => setViewMode((e as CustomEvent).detail as 'grid' | 'table');
    window.addEventListener('bundle:view-mode', handleViewMode);
    window.addEventListener('bundle:new', handleNew);
    window.addEventListener('bundle:save', handleSave);
    window.addEventListener('bundle:cancel', handleCancel);
    window.addEventListener('bundle:delete', handleDelete);
    window.addEventListener('bundle:archive', handleArchive);
    window.addEventListener('bundle:unarchive', handleUnarchive);
    window.addEventListener('bundle:trash', handleTrash);
    window.addEventListener('bundle:restore', handleRestore);
    return () => {
      window.removeEventListener('bundle:view-mode', handleViewMode);
      window.removeEventListener('bundle:new', handleNew);
      window.removeEventListener('bundle:save', handleSave);
      window.removeEventListener('bundle:cancel', handleCancel);
      window.removeEventListener('bundle:delete', handleDelete);
      window.removeEventListener('bundle:archive', handleArchive);
      window.removeEventListener('bundle:unarchive', handleUnarchive);
      window.removeEventListener('bundle:trash', handleTrash);
      window.removeEventListener('bundle:restore', handleRestore);
    };
  }, []);

  const saveBundle = async () => {
    const name = draft.name.trim();
    if (!name) { showError('Bundle name is required.'); return; }
    if (draft.price.trim() === '') { showError('Bundle price is required.'); return; }
    const price = parseFloat(draft.price) || 0;
    if (price < 0) { showError('Bundle price cannot be negative.'); return; }
    const targetCostTrimmed = draft.targetCost.trim();
    if (targetCostTrimmed !== '' && (isNaN(Number(targetCostTrimmed)) || Number(targetCostTrimmed) < 0)) {
      showError('Target cost must be a non-negative number.'); return;
    }
    const targetCost = targetCostTrimmed === '' ? null : Number(targetCostTrimmed);

    const allItems: UnifiedBundleItem[] = draft.items
      .filter(i => (i.productId || i.name.trim()) && parseFloat(i.quantity) > 0)
      .map(i => {
        if (i.productId.startsWith('p:')) {
          const prod = favourProducts.find(p => p.id === i.productId.slice(2));
          // Historically saved under a synthetic id (not the real product
          // id) — see openEditBundle's name-based reconnect on load.
          return { productId: genId(), productName: prod?.name ?? i.name, quantity: parseFloat(i.quantity), unitLabel: i.unit };
        }
        if (i.productId.startsWith('g:')) {
          // Reconnected by id on load (see openEditBundle) — the real
          // product id, unlike 'p:', so it round-trips instead of being
          // discarded for a fresh random one.
          const realId = i.productId.slice(2);
          const prod = getProduct(realId);
          return { productId: realId, productName: prod?.name ?? i.name, quantity: parseFloat(i.quantity), unitLabel: i.unit };
        }
        return { productId: genId(), productName: i.name.trim(), quantity: parseFloat(i.quantity), unitLabel: i.unit };
      });

    if (allItems.length === 0) { showError('Add at least one item to the bundle.'); return; }

    if (editingBundle) {
      let ok;
      try {
        ok = await updateBundle(editingBundle.id, {
          name,
          description: draft.description.trim() || undefined,
          price,
          items: allItems,
          types: editingBundle.types.length > 0 ? editingBundle.types : [scopeType],
          priceMode: 'manual',
          is_active: editingBundle.is_active,
          paymentReference: draft.paymentReference.trim() || undefined,
          paymentInstructions: draft.paymentInstructions.trim() || undefined,
          targetCost,
          targetCurrency: targetCost != null ? draft.targetCurrency : undefined,
        });
      } catch (err) {
        // 'Failed to update bundle.' was the only wording available before,
        // and it fired on the one case the write hadn't even been attempted.
        showError(err instanceof Error ? err.message : 'Failed to update bundle.');
        return;
      }
      if (!ok) { showError('That bundle is no longer listed — refresh and try again.'); return; }
      setBundles(loadBundles());
      showSuccess('Bundle updated.');
      closeForm();
    } else {
      try {
        await createBundle({
          name,
          description: draft.description.trim() || undefined,
          price,
          items: allItems,
          types: [scopeType],
          priceMode: 'manual',
          is_active: true,
          paymentReference: draft.paymentReference.trim() || undefined,
          paymentInstructions: draft.paymentInstructions.trim() || undefined,
          targetCost,
          targetCurrency: targetCost != null ? draft.targetCurrency : undefined,
        });
      } catch (err) {
        // Keep the form open and filled so the bundle can be retried.
        showError(err instanceof Error ? err.message : 'Failed to create bundle.');
        return;
      }
      setBundles(loadBundles());
      showSuccess('Bundle created.');
      closeForm();
    }
  };

  saveBundleRef.current = saveBundle;
  closeFormRef.current = closeForm;
  openNewBundleRef.current = openNewBundle;
  // Delete forever is only offered once a bundle is in trash (archived, then
  // trashed); the form shows Archive / Restore / Move to trash until then.
  deleteBundleRef.current = () => {
    if (editingBundle?.deletedAt) { setDeleteError(null); setDeleteTarget(editingBundle.id); }
  };

  // One step of the archive -> trash -> delete lifecycle from the open form.
  const changeBundleStage = async (step: () => Promise<unknown>, done: string) => {
    if (!editingBundle) return;
    try {
      await step();
    } catch (err) {
      showError(friendlyDeleteError(err, { record: 'bundle' }));
      return;
    }
    setBundles(loadBundles());
    void fetchArchivedBundles().then(setArchivedBundles).catch(() => {});
    void fetchTrashedBundles().then(setTrashedBundles).catch(() => {});
    showSuccess(done);
    closeForm();
  };
  archiveBundleRef.current = () => { if (editingBundle) void changeBundleStage(() => archiveBundle(editingBundle.id), 'Bundle archived.'); };
  unarchiveBundleRef.current = () => { if (editingBundle) void changeBundleStage(() => unarchiveBundle(editingBundle.id), 'Bundle restored.'); };
  trashBundleRef.current = () => { if (editingBundle) void changeBundleStage(() => trashBundle(editingBundle.id), 'Bundle moved to trash.'); };
  restoreBundleRef.current = () => { if (editingBundle) void changeBundleStage(() => restoreBundleFromTrash(editingBundle.id), 'Bundle restored from trash.'); };

  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteTarget || deleting) return;
    const b = [...bundles, ...archivedBundles, ...trashedBundles].find(x => x.id === deleteTarget);
    setDeleting(true);
    try {
      await deleteBundle(deleteTarget);
    } catch (err) {
      // Keep the dialog open with the reason so the delete can be retried or
      // given up on; closing it on a failure is what made a bundle look
      // deleted until the next refresh.
      setDeleteError(friendlyDeleteError(err, { record: 'bundle' }));
      setDeleting(false);
      return;
    }
    setDeleting(false);
    setBundles(loadBundles());
    void fetchTrashedBundles().then(setTrashedBundles).catch(() => {});
    showSuccess(`Bundle "${b?.name}" deleted.`);
    setDeleteTarget(null);
    if (showForm) closeForm();
  };

  // ── Form page ────────────────────────────────────────────────────────────────
  const formView = showForm && (
    <>
    <div className="detail-layout">
      <div className="inline-form-page">
        <div className="form-row">
          <div className="form-group">
            <label className="bv-label">Bundle name<span className="form-required">*</span></label>
            <input
              className="form-input form-input--sm"
              type="text"
              placeholder="e.g. Aso-ebi Full Set"
              value={draft.name}
              onChange={e => setDraft(prev => ({ ...prev, name: e.target.value }))}
              autoFocus
              required
            />
          </div>
          <div className="form-group">
            <label className="bv-label">Bundle price ({money.unit})<span className="form-required">*</span></label>
            {/* Shared CurrencyInput rather than a hand-formatted text box, so
                this matches every other money field (currency prefix, right-aligned,
                comma grouping) instead of being a lookalike. */}
            <CurrencyInput
              className="form-input form-input--sm"
              placeholder="0"
              value={Number(draft.price) > 0 ? Number(draft.price) : ''}
              onChange={e => setDraft(prev => ({ ...prev, price: String(Number(e.target.value) || 0) }))}
              required
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label className="bv-label">Target cost</label>
            {/* What we're sourcing against, not what's charged — separate
                from Bundle price above, and possibly a different currency
                (procurement is often costed in USD/CNY, price is always
                NGN). Left blank when this bundle has no cost ceiling; blank
                is a real, distinct state from 0. */}
            <div className="poe-amount-row">
              <SearchableSelect
                value={draft.targetCurrency}
                onChange={value => setDraft(prev => ({ ...prev, targetCurrency: value }))}
                className="poe-currency-select"
                options={CURRENCY_OPTIONS}
              />
              <CurrencyInput
                symbol=""
                className="form-input form-input--sm"
                placeholder="No target set"
                value={draft.targetCost !== '' ? Number(draft.targetCost) : ''}
                onChange={e => setDraft(prev => ({ ...prev, targetCost: e.target.value === '' ? '' : String(Number(e.target.value) || 0) }))}
              />
            </div>
          </div>
        </div>

        {/* This description is the bundle's shop-front blurb - it renders on the
            bundle card and is what a recipient reads. The old "Optional note"
            placeholder invited internal commentary (costing workings, sourcing
            flags, review actions) into a customer-facing field. Those belong on
            their own columns or on a task/insight, not here. */}
        <RichTextEditor
          label="Description"
          value={draft.description}
          onChange={v => setDraft(prev => ({ ...prev, description: v }))}
          placeholder="What this bundle includes, in the words a recipient should read"
          rows={3}
        />

        {/* A bundle priced at 0 isn't sold to guests — nothing to collect
            payment for, so a reference/instructions pair here would be
            asking for details a free/internal-only bundle never needs. */}
        {Number(draft.price) > 0 && (
          <div className="form-row">
            <div className="form-group">
              <label className="bv-label">Payment reference</label>
              <input
                className="form-input form-input--sm"
                type="text"
                placeholder="e.g. EKOCE-F"
                value={draft.paymentReference}
                onChange={e => setDraft(prev => ({ ...prev, paymentReference: e.target.value }))}
              />
            </div>
            <div className="form-group">
              <label className="bv-label">Payment instructions</label>
              <textarea
                className="form-input form-input--sm"
                rows={2}
                placeholder="e.g. Pay into Fidelity — deadline 5 Aug 2026"
                value={draft.paymentInstructions}
                onChange={e => setDraft(prev => ({ ...prev, paymentInstructions: e.target.value }))}
              />
            </div>
          </div>
        )}

        <hr className="poe-divider" />
        <div className="poe-section-header">
          <h3 className="poe-sub-heading bv-m-0">Contents</h3>
          <div className="bv-items-header-row">
            {draft.items.length > 0 && (
              <span className="poe-items-total">{draft.items.length} {draft.items.length === 1 ? 'item' : 'items'}</span>
            )}
            <button
              className="poe-add-btn bv-m-0"
              onClick={() => setDraft(prev => ({
                ...prev,
                items: [...prev.items, { id: genId(), productId: '', name: '', quantity: '', unit: 'yds' }],
              }))}
            >
              + Add Item
            </button>
          </div>
        </div>

        <LineItemsEditor
          items={draft.items.map(item => ({
            id: item.id,
            values: {
              productId: item.productId || item.name,
              quantity: item.quantity,
              unit: item.unit,
              // No reliable id-based link between a Contents row and its
              // procurement allocation (bundle.items[].productId is often a
              // synthetic fabric/favour id, a different space than
              // procurement_items.id) — matched by product name instead,
              // the convention this session's own linking work established.
              cost: formatComponentCost(item.name, bundleComponentCosts, editingBundle?.targetCurrency ?? money.currency),
            },
          }))}
          fields={[
            {
              key: 'productId', type: 'picker-or-text', header: 'Item',
              placeholder: 'Pick a favour or other product, or type a name…',
              options: [
                ...favourProducts.map(p => ({ value: `p:${p.id}`, label: p.name })),
                ...otherProducts.map(p => ({ value: `g:${p.id}`, label: p.name })),
              ],
            },
            {
              key: 'quantity', type: 'number-unit', header: 'Qty', min: '0.5', step: '0.5',
              unitKey: 'unit', unitOptions: LINE_ITEM_UNITS.map(u => ({ value: u, label: u })),
            },
            { key: 'cost', type: 'readonly', header: 'Landed Cost', width: 100 },
          ]}
          onChange={rows => setDraft(prev => ({
            ...prev,
            items: rows.map(r => {
              const raw = r.values.productId ?? '';
              const isRef = raw.startsWith('p:') || raw.startsWith('g:');
              const resolvedName = raw.startsWith('p:')
                ? favourProducts.find(p => `p:${p.id}` === raw)?.name ?? ''
                : raw.startsWith('g:')
                ? otherProducts.find(p => `g:${p.id}` === raw)?.name ?? getProduct(raw.slice(2))?.name ?? ''
                : raw;
              return {
                id: r.id,
                productId: isRef ? raw : '',
                name: resolvedName,
                quantity: r.values.quantity ?? '',
                unit: r.values.unit || 'yds',
              };
            }),
          }))}
          showAddButton={false}
          wideGrower
        />

      </div>

      <div className="detail-sidebar">
        <div className="side-section">
          <h4>Summary</h4>
          {draft.name && (
            <div className="stat-row"><span>Name</span><strong>{draft.name}</strong></div>
          )}
          {/* draft.price is a string — '0' is truthy in JS, so a plain
              truthiness check never actually hid this row for a 0-price
              bundle. A 0-price bundle isn't sold to guests, so the figure
              is meaningless here (see the same guard on Payment reference/
              Orders & Revenue below). */}
          {Number(draft.price) > 0 && (
            <div className="stat-row"><span>Price</span><strong>{money.format(Number(draft.price))}</strong></div>
          )}
          <div className="stat-row">
            <span>Items</span>
            <strong>{draft.items.filter(i => i.productId || i.name.trim()).length}</strong>
          </div>
          {editingBundle?.targetCost != null && (
            <div className="stat-row">
              <span>Target cost</span>
              <strong>{formatTargetCost(editingBundle, money.currency)}</strong>
            </div>
          )}
          {bundleActualCost && (
            <div className="stat-row">
              {/* computeBundleActualCost (ItemAllocationService.ts) already
                  spreads each allocation's order landing_cost_cents,
                  contingency_pct and tax_pct proportionally into this figure
                  — it IS the landed cost per unit, not just the raw item
                  price, so the label says so rather than leaving that
                  implicit. */}
              <span>Landed cost ({bundleActualCost.allocatedQuantity} sourced)</span>
              <strong className={editingBundle?.targetCost != null && bundleActualCost.actualCostPerUnit > editingBundle.targetCost ? 'bv-stat-danger' : undefined}>
                {formatUnitCost(bundleActualCost.actualCostPerUnit, editingBundle?.targetCurrency ?? money.currency)}
              </strong>
            </div>
          )}
          {bundleActualCost && editingBundle?.targetCost != null && editingBundle.targetCost > 0 && (() => {
            const variancePct = ((bundleActualCost.actualCostPerUnit - editingBundle.targetCost) / editingBundle.targetCost) * 100;
            const over = variancePct > 0;
            return (
              <div className="stat-row">
                <span>Variance vs target</span>
                <strong className={over ? 'bv-stat-danger' : undefined}>
                  {over ? '+' : ''}{variancePct.toFixed(1)}%
                </strong>
              </div>
            );
          })()}
        </div>

      </div>
    </div>
    {/* What the finished box should look like: reference photos for the
        packaging and contents, so the maker and the planner argue from the
        same picture. Viewers can look but not change them. */}
    <AttachmentsPanel
      entityType="bundle"
      entityId={editingBundle ? String(editingBundle.id) : undefined}
      title="Photos"
      uploadLabel="+ Add photo"
      disabledMessage="Save the bundle first to add photos."
      readOnly={!canEdit}
      readOnlyMessage={canEdit ? undefined : 'No photos yet.'}
      onError={showError}
    />
    {/* A bundle is a priced package — what is in it and what it costs to
        make get argued about; the comment feed gives that a home. */}
    <RecordActivity
      entityType="bundle"
      entityId={editingBundle ? String(editingBundle.id) : undefined}
    />
    </>
  );

  // ── Bundle card ──────────────────────────────────────────────────────────────
  const buildBundleLineItems = (bundle: UnifiedBundle) =>
    bundle.items.map(item => {
      const isYards = item.unitLabel === 'yds' || !item.unitLabel;
      if (isYards) {
        return { label: item.productName || 'Unknown fabric', qty: `${item.quantity} yds` };
      }
      return { label: item.productName || item.unitLabel || '', qty: `${item.quantity} ${item.unitLabel}` };
    });

  const renderCard = (bundle: UnifiedBundle) => (
    <ContentCard
      key={bundle.id}
      className="bv-bundle-card"
      hideImage
      title={bundle.name}
      badges={bundle.archived ? [{ label: 'Archived', type: 'warning' }] : undefined}
      description={bundle.description || ''}
      lineItems={buildBundleLineItems(bundle)}
      pricing={{
        amount: bundle.price,
        currency: money.symbol,
        label: 'Price',
        hideAmountIfZero: true,
        noteLabel: bundle.targetCost != null ? 'Target cost' : undefined,
        note: bundle.targetCost != null ? `${formatTargetCost(bundle, money.currency)} / unit` : undefined,
      }}
      onClick={() => openEditBundle(bundle)}
    />
  );

  // ── Bundle row (table view) ─────────────────────────────────────────────────
  const renderRow = (bundle: UnifiedBundle) => {
    const fabricNames = bundle.items
      .filter(i => i.unitLabel === 'yds' || !i.unitLabel)
      .map(i => i.productName || 'Unknown fabric');
    const extraCount = bundle.items.filter(i => i.unitLabel && i.unitLabel !== 'yds').length;
    return (
      <tr key={bundle.id} className="dt-row bv-row-clickable" onClick={() => openEditBundle(bundle)}>
        <td className="rec-id-cell"><RecordIdLabel kind="bundle" id={bundle.id} /></td>
        <td><strong>{bundle.name}</strong></td>
        <td className="bv-row-fabrics">{fabricNames.join(', ') || '—'}</td>
        <td>{extraCount > 0 ? extraCount : '—'}</td>
        <td>{money.format(bundle.price)}</td>
      </tr>
    );
  };

  // ── Main list ────────────────────────────────────────────────────────────────
  const bundlesByArchiveStatus =
    archiveFilter === 'active' ? bundles :
    archiveFilter === 'archived' ? archivedBundles :
    archiveFilter === 'trashed' ? trashedBundles :
    [...bundles, ...archivedBundles];
  const aseobiBundles = bundlesByArchiveStatus.filter(b => !b.types || b.types.length === 0 || b.types.includes(scopeType));

  return (
    <>
      <RecordListPublisher kind="bundle" ids={aseobiBundles.map(b => b.id)} />
      {showForm ? formView : (
        aseobiBundles.length === 0 ? (
          <EmptyState
            icon="grid"
            title={
              archiveFilter === 'archived' ? 'No archived bundles' :
              archiveFilter === 'trashed' ? 'Trash is empty' :
              `No ${scopeType} bundles yet`
            }
            description={
              archiveFilter === 'archived' ? 'Bundles appear here once archived from the list.' :
              archiveFilter === 'trashed' ? 'Bundles appear here once moved to trash from the archived list.' :
              scopeType === 'favour'
                ? 'Bundle multiple favour items into a single offer with a fixed price.'
                : 'Bundle multiple fabrics into a single offer with a fixed price. Great for aso-ebi packages.'}
            action={archiveFilter === 'archived' || archiveFilter === 'trashed' ? undefined : <button className="primary-btn" onClick={openNewBundle}>Create first bundle</button>}
          />
        ) : (
          <>
            <BundleMetricCards bundles={aseobiBundles} money={money} />
            {archiveFilter === 'active' && <BundleEconomicsPanel bundles={aseobiBundles} />}
            {viewMode === 'grid' ? (
              <div className="bv-grid">
                {aseobiBundles.map(renderCard)}
              </div>
            ) : (
              <div className="dt-wrap">
                <div className="dt-scroll ds-scroll-x ds-scroll-x--framed">
                  <table className="dt dt--hoverable">
                    <thead>
                      <tr>
                        <th>ID</th>
                        <th>Bundle</th>
                        <th>Fabrics</th>
                        <th>Extras</th>
                        <th>Price</th>
                      </tr>
                    </thead>
                    <tbody>
                      {aseobiBundles.map(renderRow)}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )
      )}
      <ConfirmDialog
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        title="Delete this bundle forever?"
        subject={[...bundles, ...archivedBundles, ...trashedBundles].find(x => x.id === deleteTarget)?.name}
        description="This cannot be undone."
        consequences={['The bundle and the items listed in it', 'Its price and payment instructions']}
        error={deleteError}
        confirmLabel="Delete forever"
        loading={deleting}
      />
    </>
  );
};

export default BundlesView;
